import { TileType } from './MapTemplates';
import { TILE_COLORS } from './IsometricMap';

// ============================================================
// Hash Noise & Color Helpers
// ============================================================

/** Fast deterministic hash noise – returns 0..1 */
function hashNoise(px: number, py: number, seed: number): number {
  let h = (seed * 374761393 + px * 668265263 + py * 1274126177) | 0;
  h = ((h ^ (h >> 13)) * 1103515245) | 0;
  h = (h ^ (h >> 16)) | 0;
  return (h & 0x7fffffff) / 0x7fffffff;
}

// Fast floor that works for negative numbers (offset trick avoids Math.floor)
const _FLOOR_OFFSET = 1 << 20;
function fastFloor(v: number): number { return ((v + _FLOOR_OFFSET) | 0) - _FLOOR_OFFSET; }

/** Bilinear interpolated smooth noise */
function smoothNoise(x: number, y: number, seed: number): number {
  const ix = fastFloor(x), iy = fastFloor(y);
  const fx = x - ix, fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx); // smoothstep
  const sy = fy * fy * (3 - 2 * fy);
  const n00 = hashNoise(ix, iy, seed);
  const n10 = hashNoise(ix + 1, iy, seed);
  const n01 = hashNoise(ix, iy + 1, seed);
  const n11 = hashNoise(ix + 1, iy + 1, seed);
  return n00 * (1 - sx) * (1 - sy) + n10 * sx * (1 - sy) + n01 * (1 - sx) * sy + n11 * sx * sy;
}

type RGB = [number, number, number];

function hexToRgb(hex: string): RGB {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff];
}

// Noise intensity per tile type (how much variation between light/dark)
const NOISE_INTENSITY: Partial<Record<TileType, number>> = {
  [TileType.SNOW]: 0.25,
  [TileType.LAVA]: 0.9,
  [TileType.VOID]: 0.8,
  [TileType.WATER]: 0.35,
  [TileType.DEEP_WATER]: 0.3,
  [TileType.SHALLOW_WATER]: 0.4,
  [TileType.SWAMP_WATER]: 0.45,
  [TileType.SAND]: 0.4,
  [TileType.MUD]: 0.5,
  [TileType.STONE]: 0.55,
  [TileType.ROCKY_GROUND]: 0.6,
  [TileType.CLIFF_EDGE]: 0.55,
};
const DEFAULT_NOISE_INTENSITY = 0.5;

// ============================================================
// Precomputed RGB cache (avoids per-pixel hex parsing)
// ============================================================
const tileRgbCache: Map<number, { light: RGB; dark: RGB }> = new Map();

function getTileRgb(type: TileType): { light: RGB; dark: RGB } {
  let cached = tileRgbCache.get(type);
  if (!cached) {
    const colors = TILE_COLORS[type];
    cached = { light: hexToRgb(colors.light), dark: hexToRgb(colors.dark) };
    tileRgbCache.set(type, cached);
  }
  return cached;
}

export function clearProceduralCaches(): void {
  tileRgbCache.clear();
}

// ============================================================
// Blend direction constants (hoisted to avoid per-pixel allocation)
// ============================================================
const BLEND_NDX = [1, 0, -1, 0, 1, -1, -1, 1] as const;
const BLEND_NDY = [0, 1, 0, -1, 1, 1, -1, -1] as const;

// ============================================================
// Continuous Terrain Buffer Renderer (screen-space, no tile seams)
// ============================================================

/**
 * Fills a pixel buffer with continuous terrain colors.
 * Renders in screen-space with world-coordinate noise — no individual tile
 * canvases, so tile boundaries are completely invisible.
 *
 * @param data        - Uint8ClampedArray from ImageData
 * @param bufW/bufH   - Buffer dimensions (typically screen / scale)
 * @param originWX/WY - World coordinates at buffer pixel (0,0)
 * @param step        - World units per buffer pixel (= scale / zoom)
 * @param getTileType - Tile lookup function
 */
export function renderTerrainBuffer(
  data: Uint8ClampedArray,
  bufW: number,
  bufH: number,
  originWX: number,
  originWY: number,
  step: number,
  getTileType: (tx: number, ty: number) => TileType | null,
  tileWidth: number,
  tileHeight: number,
  getElevation?: (tx: number, ty: number) => number,
): void {
  const halfTW = tileWidth / 2;
  const halfTH = tileHeight / 2;
  const invHalfTW = 1 / halfTW;
  const invHalfTH = 1 / halfTH;
  const noiseInvScale = 1 / 5;
  const blendZone = 0.22;

  // Directional lighting constants for slope shading
  const LIGHT_DX = 0.707;       // Light direction: top-right (standard isometric)
  const LIGHT_DY = -0.707;
  const SLOPE_STRENGTH = 1.8;   // Slope light/shadow contrast
  const AMBIENT_STRENGTH = 0.15; // Height-based brightness bias
  const WARM_SHIFT = 0.16;      // Warm tint on sun-facing slopes (adds gold/yellow)
  const COOL_SHIFT = 0.14;      // Cool tint in shadows (adds blue)
  const AO_STRENGTH = 0.50;     // Ambient occlusion in concave areas
  const RIDGE_BOOST = 0.12;     // Extra highlight on convex ridges

  // Tile-space increments per buffer pixel
  // X-axis: worldX increases by step, worldY constant
  const dftx_dbx = step * invHalfTW * 0.5;
  const dfty_dbx = -step * invHalfTW * 0.5;
  // Y-axis: worldY increases by step, worldX constant
  const dftx_dby = step * invHalfTH * 0.5;
  const dfty_dby = step * invHalfTH * 0.5;

  // Starting fractional tile coords at buffer (0,0)
  const ftx0 = (originWX * invHalfTW + originWY * invHalfTH) * 0.5;
  const fty0 = (originWY * invHalfTH - originWX * invHalfTW) * 0.5;

  // Uint32Array view for single-write pixels (4x fewer byte operations)
  const buf32 = new Uint32Array(data.buffer);

  // Per-tile caches: avoid redundant lookups for pixels in the same tile
  let prevTX = -99999, prevTY = -99999;
  let ce00 = 0, ce10 = 0, ce01 = 0, ce11 = 0, ceN = 0, ceW = 0;
  let prevType: TileType | null = null;
  let cDarkR = 0, cDarkG = 0, cDarkB = 0;
  let cLightR = 0, cLightG = 0, cLightB = 0;
  let cIntensity = DEFAULT_NOISE_INTENSITY;
  let cIsWater = false;
  let cLaplacian = 0;

  for (let by = 0; by < bufH; by++) {
    const wy = originWY + by * step;
    let wx = originWX;
    let ftx = ftx0 + by * dftx_dby;
    let fty = fty0 + by * dfty_dby;

    for (let bx = 0; bx < bufW; bx++) {
      const tileX = fastFloor(ftx);
      const tileY = fastFloor(fty);

      const type = getTileType(tileX, tileY);
      if (type === null) {
        wx += step;
        ftx += dftx_dbx;
        fty += dfty_dbx;
        continue;
      }

      // Cache tile RGB + intensity (avoids Map lookup + hex parse for same tile type)
      if (type !== prevType) {
        const tileRgb = getTileRgb(type);
        cDarkR = tileRgb.dark[0]; cDarkG = tileRgb.dark[1]; cDarkB = tileRgb.dark[2];
        cLightR = tileRgb.light[0]; cLightG = tileRgb.light[1]; cLightB = tileRgb.light[2];
        cIntensity = NOISE_INTENSITY[type] ?? DEFAULT_NOISE_INTENSITY;
        cIsWater = type === TileType.WATER || type === TileType.DEEP_WATER ||
                   type === TileType.SHALLOW_WATER || type === TileType.SWAMP_WATER;
        prevType = type;
      }

      const n = smoothNoise(wx * noiseInvScale, wy * noiseInvScale, 42);
      const t = 0.5 + (n - 0.5) * cIntensity;
      // Inline lerpColor — avoids allocating a new RGB array per pixel
      let r = cDarkR + (cLightR - cDarkR) * t;
      let g = cDarkG + (cLightG - cDarkG) * t;
      let b = cDarkB + (cLightB - cDarkB) * t;

      const fracX = ftx - tileX;
      const fracY = fty - tileY;

      // Slope-based directional lighting (skip water tiles)
      if (getElevation && !cIsWater) {
        // Cache 6-point elevation stencil per tile (many pixels share the same tile)
        if (tileX !== prevTX || tileY !== prevTY) {
          ce00 = getElevation(tileX, tileY);
          ce10 = getElevation(tileX + 1, tileY);
          ce01 = getElevation(tileX, tileY + 1);
          ce11 = getElevation(tileX + 1, tileY + 1);
          ceN  = getElevation(tileX, tileY - 1);
          ceW  = getElevation(tileX - 1, tileY);
          cLaplacian = (ce10 + ceW - 2 * ce00) + (ce01 + ceN - 2 * ce00);
          prevTX = tileX;
          prevTY = tileY;
        }

        // Interpolated elevation at this sub-tile position
        const oneMinusFX = 1 - fracX;
        const oneMinusFY = 1 - fracY;
        const elevHere = ce00 * oneMinusFX * oneMinusFY
                       + ce10 * fracX * oneMinusFY
                       + ce01 * oneMinusFX * fracY
                       + ce11 * fracX * fracY;

        // Analytic gradient (partial derivatives)
        const dhdtx = (ce10 - ce00) * oneMinusFY + (ce11 - ce01) * fracY;
        const dhdty = (ce01 - ce00) * oneMinusFX + (ce11 - ce10) * fracX;

        // Tile-space -> world-space gradient
        const dhdwx = (dhdtx - dhdty) * invHalfTW * 0.5;
        const dhdwy = (dhdtx + dhdty) * invHalfTH * 0.5;

        // Add micro-noise perturbation to gradient for organic look
        const perturbNoise = (hashNoise(
          (wx * 2.7) | 0, (wy * 2.7) | 0, 5531
        ) - 0.5) * 0.015;

        // Slope dot light direction
        const slopeDot = (dhdwx + perturbNoise) * LIGHT_DX + (dhdwy + perturbNoise) * LIGHT_DY;

        // AO: concave areas get darker, convex get slight boost
        const aoFactor = cLaplacian < 0
          ? cLaplacian * AO_STRENGTH
          : cLaplacian * RIDGE_BOOST;

        // Ambient bias: higher = brighter, lower = darker
        const ambientBias = (elevHere - 0.45) * AMBIENT_STRENGTH;

        // Final brightness multiplier (non-linear: dramatic shadows, gentle highlights)
        const rawBright = 1.0 + slopeDot * SLOPE_STRENGTH + ambientBias + aoFactor;
        const brightness = rawBright < 1.0
          ? Math.max(0.45, 0.45 + 0.55 * rawBright)
          : Math.min(1.65, 1.0 + (rawBright - 1.0) * 0.65);

        r *= brightness;
        g *= brightness;
        b *= brightness;

        // Color temperature shift: warm on lit slopes, cool in shadow
        if (slopeDot > 0.01) {
          const warmT = Math.min(1, slopeDot * 3) * WARM_SHIFT;
          r += warmT * 40;
          g += warmT * 25;
          b -= warmT * 10;
        } else if (slopeDot < -0.01) {
          const coolT = Math.min(1, -slopeDot * 3) * COOL_SHIFT;
          r -= coolT * 8;
          g -= coolT * 4;
          b += coolT * 25;
        }
      }

      // Edge blending with neighboring tiles
      const oneMinusFracX = 1 - fracX;
      const oneMinusFracY = 1 - fracY;
      const minDist = Math.min(fracX, fracY, oneMinusFracX, oneMinusFracY);

      if (minDist < blendZone) {
        const edgeNoise = (hashNoise(
          (wx * 3) | 0, (wy * 3) | 0, 9999
        ) - 0.5) * blendZone * 0.4;

        let totalWeight = 1;

        // Cardinal edge distances only (diagonal blending removed for perf)
        const bd0 = oneMinusFracX;                             // E
        const bd1 = oneMinusFracY;                             // S
        const bd2 = fracX;                                     // W
        const bd3 = fracY;                                     // N

        // Unrolled blend loop — uses module-scope BLEND_NDX/NDY, avoids dists[] allocation
        let adjDist: number, nType: TileType | null, st: number, smooth: number, weight: number;
        let nRgb: { light: RGB; dark: RGB }, nbt: number;

        adjDist = bd0 + edgeNoise;
        if (adjDist < blendZone) { nType = getTileType(tileX + BLEND_NDX[0], tileY + BLEND_NDY[0]); if (nType !== null && nType !== type) { st = adjDist / blendZone; if (st < 0) st = 0; smooth = st * st * (3 - 2 * st); weight = 1 - smooth; nRgb = getTileRgb(nType); nbt = 0.5 + (n - 0.5) * (NOISE_INTENSITY[nType] ?? DEFAULT_NOISE_INTENSITY); r += (nRgb.dark[0] + (nRgb.light[0] - nRgb.dark[0]) * nbt) * weight; g += (nRgb.dark[1] + (nRgb.light[1] - nRgb.dark[1]) * nbt) * weight; b += (nRgb.dark[2] + (nRgb.light[2] - nRgb.dark[2]) * nbt) * weight; totalWeight += weight; } }

        adjDist = bd1 + edgeNoise;
        if (adjDist < blendZone) { nType = getTileType(tileX + BLEND_NDX[1], tileY + BLEND_NDY[1]); if (nType !== null && nType !== type) { st = adjDist / blendZone; if (st < 0) st = 0; smooth = st * st * (3 - 2 * st); weight = 1 - smooth; nRgb = getTileRgb(nType); nbt = 0.5 + (n - 0.5) * (NOISE_INTENSITY[nType] ?? DEFAULT_NOISE_INTENSITY); r += (nRgb.dark[0] + (nRgb.light[0] - nRgb.dark[0]) * nbt) * weight; g += (nRgb.dark[1] + (nRgb.light[1] - nRgb.dark[1]) * nbt) * weight; b += (nRgb.dark[2] + (nRgb.light[2] - nRgb.dark[2]) * nbt) * weight; totalWeight += weight; } }

        adjDist = bd2 + edgeNoise;
        if (adjDist < blendZone) { nType = getTileType(tileX + BLEND_NDX[2], tileY + BLEND_NDY[2]); if (nType !== null && nType !== type) { st = adjDist / blendZone; if (st < 0) st = 0; smooth = st * st * (3 - 2 * st); weight = 1 - smooth; nRgb = getTileRgb(nType); nbt = 0.5 + (n - 0.5) * (NOISE_INTENSITY[nType] ?? DEFAULT_NOISE_INTENSITY); r += (nRgb.dark[0] + (nRgb.light[0] - nRgb.dark[0]) * nbt) * weight; g += (nRgb.dark[1] + (nRgb.light[1] - nRgb.dark[1]) * nbt) * weight; b += (nRgb.dark[2] + (nRgb.light[2] - nRgb.dark[2]) * nbt) * weight; totalWeight += weight; } }

        adjDist = bd3 + edgeNoise;
        if (adjDist < blendZone) { nType = getTileType(tileX + BLEND_NDX[3], tileY + BLEND_NDY[3]); if (nType !== null && nType !== type) { st = adjDist / blendZone; if (st < 0) st = 0; smooth = st * st * (3 - 2 * st); weight = 1 - smooth; nRgb = getTileRgb(nType); nbt = 0.5 + (n - 0.5) * (NOISE_INTENSITY[nType] ?? DEFAULT_NOISE_INTENSITY); r += (nRgb.dark[0] + (nRgb.light[0] - nRgb.dark[0]) * nbt) * weight; g += (nRgb.dark[1] + (nRgb.light[1] - nRgb.dark[1]) * nbt) * weight; b += (nRgb.dark[2] + (nRgb.light[2] - nRgb.dark[2]) * nbt) * weight; totalWeight += weight; } }

        // Diagonal neighbors removed for performance (cardinal-only blending)

        r /= totalWeight;
        g /= totalWeight;
        b /= totalWeight;
      }

      // Single 32-bit pixel write instead of 4 separate byte writes
      // Clamp to 0-255 and pack as ABGR (little-endian)
      let ri = r | 0; if (ri > 255) ri = 255; else if (ri < 0) ri = 0;
      let gi = g | 0; if (gi > 255) gi = 255; else if (gi < 0) gi = 0;
      let bi = b | 0; if (bi > 255) bi = 255; else if (bi < 0) bi = 0;
      buf32[by * bufW + bx] = 0xFF000000 | (bi << 16) | (gi << 8) | ri;

      wx += step;
      ftx += dftx_dbx;
      fty += dfty_dbx;
    }
  }
}

// ============================================================
// Tile Detail Overlay (per-tile decorative elements on top of terrain)
// ============================================================

export function renderTileDetailOverlay(
  ctx: CanvasRenderingContext2D,
  type: TileType,
  screenX: number,
  screenY: number,
  halfW: number,
  halfH: number,
  zoom: number,
  tileX: number,
  tileY: number,
): void {
  const tileSeed = tileX * 7919 + tileY * 6271;
  // No clip — detail elements stay within diamond via detailPos (60% radius)
  drawTileDetail(ctx, type, screenX, screenY, halfW, halfH, zoom, tileSeed);
}

// ============================================================
// Type-Specific Detail Functions
// ============================================================

function drawTileDetail(
  ctx: CanvasRenderingContext2D,
  type: TileType,
  cx: number, cy: number,
  halfW: number, halfH: number,
  zoom: number,
  seed: number,
): void {
  switch (type) {
    case TileType.GRASS: drawGrass(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.DARK_GRASS: drawDarkGrass(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.DIRT: drawDirt(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.STONE: drawStone(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.ROCKY_GROUND: drawRockyGround(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.SAND: drawSand(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.MUD: drawMud(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.SNOW: drawSnow(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.LAVA: drawLava(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.VOID: drawVoid(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.HILL: drawHill(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.MOUNTAIN: drawMountain(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.WATER: drawWater(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.SHALLOW_WATER: drawShallowWater(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.DEEP_WATER: drawDeepWater(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.SWAMP_WATER: drawSwampWater(ctx, cx, cy, halfW, halfH, zoom, seed); break;
    case TileType.CLIFF_EDGE: drawCliffEdge(ctx, cx, cy, halfW, halfH, zoom, seed); break;
  }
}

// --- Helper: deterministic position inside diamond ---
function detailPos(cx: number, cy: number, halfW: number, halfH: number, seed: number, i: number): [number, number] {
  const a = hashNoise(i, 0, seed) * 2 - 1; // -1..1
  const b = hashNoise(0, i, seed) * 2 - 1;
  // Shrink to stay inside diamond (70% radius)
  const scale = 0.6;
  const x = cx + a * halfW * scale;
  const y = cy + b * halfH * scale;
  return [x, y];
}

// ---- GRASS ----
function drawGrass(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  const count = 5 + Math.floor(hashNoise(3, 7, seed) * 4); // 5-8
  ctx.lineWidth = Math.max(1, zoom * 0.8);
  for (let i = 0; i < count; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const h = (3 + hashNoise(i, 5, seed) * 4) * zoom;
    const lean = (hashNoise(i, 9, seed) - 0.5) * 3 * zoom;
    const green = 80 + Math.floor(hashNoise(i, 2, seed) * 60);
    ctx.strokeStyle = `rgba(${30 + Math.floor(hashNoise(i, 3, seed) * 40)},${green},${20 + Math.floor(hashNoise(i, 4, seed) * 20)},0.6)`;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + lean, y - h);
    ctx.stroke();
  }
  // Occasional tiny flower dot
  if (hashNoise(1, 1, seed) > 0.7) {
    const [fx, fy] = detailPos(cx, cy, halfW, halfH, seed, 50);
    ctx.fillStyle = hashNoise(2, 2, seed) > 0.5 ? 'rgba(255,220,60,0.7)' : 'rgba(220,80,80,0.7)';
    ctx.beginPath();
    ctx.arc(fx, fy, Math.max(1, zoom * 1.2), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- DARK GRASS ----
function drawDarkGrass(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  const count = 3 + Math.floor(hashNoise(3, 7, seed) * 3); // 3-5
  ctx.lineWidth = Math.max(1, zoom * 0.8);
  for (let i = 0; i < count; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const h = (2.5 + hashNoise(i, 5, seed) * 3) * zoom;
    const lean = (hashNoise(i, 9, seed) - 0.5) * 2.5 * zoom;
    ctx.strokeStyle = `rgba(20,${50 + Math.floor(hashNoise(i, 2, seed) * 30)},15,0.5)`;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + lean, y - h);
    ctx.stroke();
  }
  // Shadow dots
  for (let i = 0; i < 2; i++) {
    const [sx, sy] = detailPos(cx, cy, halfW, halfH, seed, i + 30);
    ctx.fillStyle = 'rgba(0,0,0,0.12)';
    ctx.beginPath();
    ctx.arc(sx, sy, Math.max(1, zoom * 1.5), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- DIRT ----
function drawDirt(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Pebble dots
  const pebbleCount = 3 + Math.floor(hashNoise(5, 3, seed) * 3);
  for (let i = 0; i < pebbleCount; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 20);
    const r = (1 + hashNoise(i, 6, seed) * 1.5) * zoom;
    const bright = 90 + Math.floor(hashNoise(i, 7, seed) * 40);
    ctx.fillStyle = `rgba(${bright},${bright - 15},${bright - 30},0.5)`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Thin crack lines
  for (let i = 0; i < 2; i++) {
    const [x1, y1] = detailPos(cx, cy, halfW, halfH, seed, i + 40);
    const dx = (hashNoise(i, 11, seed) - 0.5) * halfW * 0.4;
    const dy = (hashNoise(i, 12, seed) - 0.5) * halfH * 0.3;
    ctx.strokeStyle = 'rgba(60,40,25,0.25)';
    ctx.lineWidth = Math.max(0.5, zoom * 0.5);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x1 + dx, y1 + dy);
    ctx.stroke();
  }
}

// ---- STONE ----
function drawStone(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // 2-3 crack lines
  const crackCount = 2 + (hashNoise(1, 3, seed) > 0.5 ? 1 : 0);
  for (let i = 0; i < crackCount; i++) {
    const [x1, y1] = detailPos(cx, cy, halfW, halfH, seed, i + 15);
    const dx = (hashNoise(i, 20, seed) - 0.5) * halfW * 0.6;
    const dy = (hashNoise(i, 21, seed) - 0.5) * halfH * 0.5;
    const midX = x1 + dx * 0.5 + (hashNoise(i, 22, seed) - 0.5) * 3 * zoom;
    const midY = y1 + dy * 0.5 + (hashNoise(i, 23, seed) - 0.5) * 3 * zoom;
    // Thick crack
    ctx.strokeStyle = 'rgba(30,30,30,0.3)';
    ctx.lineWidth = Math.max(1, zoom * 1.2);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.quadraticCurveTo(midX, midY, x1 + dx, y1 + dy);
    ctx.stroke();
    // Thin highlight beside crack
    ctx.strokeStyle = 'rgba(180,180,180,0.2)';
    ctx.lineWidth = Math.max(0.5, zoom * 0.5);
    ctx.beginPath();
    ctx.moveTo(x1 + zoom, y1 - zoom);
    ctx.quadraticCurveTo(midX + zoom, midY - zoom, x1 + dx + zoom, y1 + dy - zoom);
    ctx.stroke();
  }
  // Quartz speck
  if (hashNoise(7, 7, seed) > 0.6) {
    const [qx, qy] = detailPos(cx, cy, halfW, halfH, seed, 55);
    ctx.fillStyle = 'rgba(220,220,240,0.5)';
    ctx.beginPath();
    ctx.arc(qx, qy, Math.max(0.8, zoom * 0.8), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- ROCKY GROUND ----
function drawRockyGround(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Stone cracks
  drawStone(ctx, cx, cy, halfW, halfH, zoom, seed + 100);
  // Small rock shapes
  for (let i = 0; i < 2; i++) {
    const [rx, ry] = detailPos(cx, cy, halfW, halfH, seed, i + 60);
    const rw = (2 + hashNoise(i, 30, seed) * 2) * zoom;
    const rh = (1.5 + hashNoise(i, 31, seed) * 1) * zoom;
    ctx.fillStyle = `rgba(${80 + Math.floor(hashNoise(i, 32, seed) * 30)},${80 + Math.floor(hashNoise(i, 33, seed) * 30)},${80 + Math.floor(hashNoise(i, 34, seed) * 30)},0.4)`;
    ctx.beginPath();
    ctx.ellipse(rx, ry, rw, rh, hashNoise(i, 35, seed) * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- SAND ----
function drawSand(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Sand grain dots
  for (let i = 0; i < 6; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    ctx.fillStyle = `rgba(${180 + Math.floor(hashNoise(i, 5, seed) * 40)},${160 + Math.floor(hashNoise(i, 6, seed) * 30)},${120 + Math.floor(hashNoise(i, 7, seed) * 30)},0.35)`;
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.5, zoom * 0.6), 0, Math.PI * 2);
    ctx.fill();
  }
  // Wind wave line
  const waveY = cy + (hashNoise(3, 3, seed) - 0.5) * halfH * 0.4;
  ctx.strokeStyle = 'rgba(200,180,140,0.25)';
  ctx.lineWidth = Math.max(0.5, zoom * 0.7);
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.5, waveY);
  ctx.quadraticCurveTo(cx, waveY - 2 * zoom, cx + halfW * 0.5, waveY + zoom);
  ctx.stroke();
}

// ---- MUD ----
function drawMud(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Wet dark blotches
  for (let i = 0; i < 3; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const r = (2 + hashNoise(i, 8, seed) * 3) * zoom;
    ctx.fillStyle = 'rgba(30,20,10,0.18)';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Wet reflection speck
  if (hashNoise(5, 5, seed) > 0.5) {
    const [rx, ry] = detailPos(cx, cy, halfW, halfH, seed, 45);
    ctx.fillStyle = 'rgba(200,200,220,0.25)';
    ctx.beginPath();
    ctx.arc(rx, ry, Math.max(0.8, zoom * 1), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- SNOW ----
function drawSnow(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Blue shadow spots
  for (let i = 0; i < 2; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const r = (2 + hashNoise(i, 4, seed) * 2) * zoom;
    ctx.fillStyle = 'rgba(160,180,220,0.12)';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // White sparkle dot
  if (hashNoise(3, 3, seed) > 0.4) {
    const [sx, sy] = detailPos(cx, cy, halfW, halfH, seed, 40);
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.beginPath();
    ctx.arc(sx, sy, Math.max(0.5, zoom * 0.7), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- LAVA ----
function drawLava(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Bright veins
  for (let i = 0; i < 3; i++) {
    const [x1, y1] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const dx = (hashNoise(i, 15, seed) - 0.5) * halfW * 0.5;
    const dy = (hashNoise(i, 16, seed) - 0.5) * halfH * 0.4;
    const brightness = hashNoise(i, 17, seed);
    const r = 255;
    const g = Math.floor(100 + brightness * 155);
    const b = Math.floor(brightness * 50);
    ctx.strokeStyle = `rgba(${r},${g},${b},0.6)`;
    ctx.lineWidth = Math.max(1, zoom * (1 + hashNoise(i, 18, seed)));
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x1 + dx, y1 + dy);
    ctx.stroke();
  }
  // Dark crust blotches
  for (let i = 0; i < 2; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 30);
    const r = (1.5 + hashNoise(i, 19, seed) * 2) * zoom;
    ctx.fillStyle = 'rgba(80,20,0,0.35)';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- VOID ----
function drawVoid(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Nebula glow spots
  for (let i = 0; i < 3; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const r = (3 + hashNoise(i, 5, seed) * 3) * zoom;
    const hue = hashNoise(i, 6, seed) > 0.5 ? '120,80,200' : '60,100,220';
    ctx.fillStyle = `rgba(${hue},0.12)`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Faint sparkle
  if (hashNoise(8, 8, seed) > 0.5) {
    const [sx, sy] = detailPos(cx, cy, halfW, halfH, seed, 50);
    ctx.fillStyle = 'rgba(180,160,255,0.35)';
    ctx.beginPath();
    ctx.arc(sx, sy, Math.max(0.5, zoom * 0.6), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- HILL ----
function drawHill(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Grass blades (reuse grass logic)
  drawGrass(ctx, cx, cy, halfW, halfH, zoom, seed + 200);
  // Contour line for elevation feel
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = Math.max(1, zoom * 1.2);
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.5, cy + halfH * 0.1);
  ctx.quadraticCurveTo(cx, cy - halfH * 0.2, cx + halfW * 0.5, cy + halfH * 0.1);
  ctx.stroke();
}

// ---- MOUNTAIN ----
function drawMountain(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, _seed: number): void {
  // Mountain peak shape
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.3, cy);
  ctx.lineTo(cx, cy - halfH * 0.8);
  ctx.lineTo(cx + halfW * 0.3, cy);
  ctx.closePath();
  ctx.fillStyle = '#6a6a6a';
  ctx.fill();
  // Snow cap
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.1, cy - halfH * 0.5);
  ctx.lineTo(cx, cy - halfH * 0.8);
  ctx.lineTo(cx + halfW * 0.1, cy - halfH * 0.5);
  ctx.closePath();
  ctx.fillStyle = '#e8e8f0';
  ctx.fill();
  // Rock texture dots
  for (let i = 0; i < 3; i++) {
    const rx = cx + (hashNoise(i, 1, _seed) - 0.5) * halfW * 0.3;
    const ry = cy - halfH * 0.1 + (hashNoise(i, 2, _seed) - 0.5) * halfH * 0.3;
    ctx.fillStyle = 'rgba(50,50,50,0.25)';
    ctx.beginPath();
    ctx.arc(rx, ry, Math.max(0.5, zoom * 0.8), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ---- WATER ----
function drawWater(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // 2-3 static wave lines
  const waveCount = 2 + (hashNoise(1, 1, seed) > 0.5 ? 1 : 0);
  for (let i = 0; i < waveCount; i++) {
    const yOff = (i - 1) * halfH * 0.25;
    ctx.strokeStyle = `rgba(100,180,255,${0.15 + hashNoise(i, 3, seed) * 0.1})`;
    ctx.lineWidth = Math.max(0.5, zoom * 0.7);
    ctx.beginPath();
    ctx.moveTo(cx - halfW * 0.5, cy + yOff);
    ctx.quadraticCurveTo(cx, cy + yOff - 2 * zoom, cx + halfW * 0.5, cy + yOff);
    ctx.stroke();
  }
}

// ---- SHALLOW WATER ----
function drawShallowWater(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // More prominent waves
  for (let i = 0; i < 3; i++) {
    const yOff = (i - 1) * halfH * 0.22;
    ctx.strokeStyle = `rgba(130,200,240,${0.2 + hashNoise(i, 3, seed) * 0.1})`;
    ctx.lineWidth = Math.max(0.8, zoom * 0.9);
    ctx.beginPath();
    ctx.moveTo(cx - halfW * 0.55, cy + yOff);
    ctx.quadraticCurveTo(cx, cy + yOff - 2.5 * zoom, cx + halfW * 0.55, cy + yOff);
    ctx.stroke();
  }
  // Sandy undertone
  ctx.fillStyle = 'rgba(180,160,120,0.08)';
  ctx.beginPath();
  ctx.ellipse(cx, cy + halfH * 0.2, halfW * 0.3, halfH * 0.15, 0, 0, Math.PI * 2);
  ctx.fill();
}

// ---- DEEP WATER ----
function drawDeepWater(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Minimal – 1-2 deep highlights
  for (let i = 0; i < 2; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    ctx.strokeStyle = 'rgba(80,130,200,0.15)';
    ctx.lineWidth = Math.max(0.5, zoom * 0.6);
    ctx.beginPath();
    ctx.moveTo(x - 3 * zoom, y);
    ctx.lineTo(x + 3 * zoom, y);
    ctx.stroke();
  }
}

// ---- SWAMP WATER ----
function drawSwampWater(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, halfH: number, zoom: number, seed: number): void {
  // Murky green blotches
  for (let i = 0; i < 2; i++) {
    const [x, y] = detailPos(cx, cy, halfW, halfH, seed, i + 10);
    const r = (2 + hashNoise(i, 5, seed) * 2) * zoom;
    ctx.fillStyle = 'rgba(40,80,30,0.15)';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Lily pad circle
  if (hashNoise(6, 6, seed) > 0.5) {
    const [lx, ly] = detailPos(cx, cy, halfW, halfH, seed, 45);
    const lr = (2 + hashNoise(7, 7, seed)) * zoom;
    ctx.fillStyle = 'rgba(50,100,40,0.35)';
    ctx.beginPath();
    ctx.arc(lx, ly, lr, 0, Math.PI * 2);
    ctx.fill();
    // Small gap in lily pad
    ctx.strokeStyle = 'rgba(30,60,30,0.3)';
    ctx.lineWidth = Math.max(0.5, zoom * 0.5);
    ctx.beginPath();
    ctx.moveTo(lx, ly);
    ctx.lineTo(lx + lr, ly);
    ctx.stroke();
  }
}

// ---- CLIFF EDGE ----
function drawCliffEdge(ctx: CanvasRenderingContext2D, cx: number, cy: number, halfW: number, _halfH: number, zoom: number, _seed: number): void {
  // Horizontal ledge line
  ctx.strokeStyle = 'rgba(100,90,70,0.5)';
  ctx.lineWidth = Math.max(1, zoom * 1.5);
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.6, cy);
  ctx.lineTo(cx + halfW * 0.6, cy);
  ctx.stroke();
  // Bottom shadow
  ctx.strokeStyle = 'rgba(0,0,0,0.15)';
  ctx.lineWidth = Math.max(1, zoom * 2);
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.5, cy + 2 * zoom);
  ctx.lineTo(cx + halfW * 0.5, cy + 2 * zoom);
  ctx.stroke();
  // Top highlight
  ctx.strokeStyle = 'rgba(255,255,255,0.15)';
  ctx.lineWidth = Math.max(0.5, zoom * 0.8);
  ctx.beginPath();
  ctx.moveTo(cx - halfW * 0.55, cy - zoom);
  ctx.lineTo(cx + halfW * 0.55, cy - zoom);
  ctx.stroke();
}

